import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

export type LiveEngine = 'whisper' | 'voxtral';

export interface ChannelEngine {
  channel: string;
  /** Which engine transcribes this channel live by default. */
  primaryEngine: LiveEngine;
  /**
   * false when nobody has chosen an engine for this channel and the value
   * shown comes from the backend's configured default (ORFS = Voxtral).
   */
  isExplicit: boolean;
}

/** What a channel switch did to the broadcasts already in the plan. */
export interface ChannelEngineRollout {
  channel: string;
  engine: LiveEngine;
  /** Upcoming KIUT + Live broadcasts considered (rest of today + tomorrow). */
  scanned: number;
  /** Newly scheduled for Voxtral. */
  armed: number;
  /** Already scheduled but missing a FAB target — filled in. */
  fabBackfilled: number;
  /** Stood down from Voxtral back to Whisper. */
  reverted: number;
  /** Left alone — already correct, operator decision, or PCMS/VOD served. */
  skipped: number;
}

export interface ChannelEngineChange extends ChannelEngine {
  rollout: ChannelEngineRollout;
}

/**
 * 2026-09-07 — per-channel settings for the admin dashboard.
 *
 * Setting a channel to Voxtral means every broadcast flagged KIUT from then on
 * is armed for Voxtral automatically instead of falling through to Whisper.
 *
 * 2026-09-09 — the switch also rolls onto broadcasts already in the plan that
 * have not aired yet, so changing a channel in the morning does not leave the
 * rest of the day on the old engine. The response carries what it did.
 */
@Injectable({ providedIn: 'root' })
export class ChannelSettingsService {

  private readonly baseUrl = environment.API_Base;

  constructor(private http: HttpClient) {}

  list(): Observable<ChannelEngine[]> {
    return this.http.get<ChannelEngine[]>(`${this.baseUrl}/admin/channels`);
  }

  setPrimaryEngine(channel: string, engine: LiveEngine): Observable<ChannelEngineChange> {
    return this.http.put<ChannelEngineChange>(
      `${this.baseUrl}/admin/channels/${encodeURIComponent(channel)}/primary-engine`,
      { engine });
  }
}
